import { useEffect, useState } from 'react';

import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay
} from 'date-fns';
import EventBus from 'lib/EventBus';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';

import { DutyLinesProgress } from '../types';

import { formatByLocale } from 'utils/date';

import { getLastLockedMonth } from 'RestAPIs/last-locked-month';

import {
  canDownloadReportAtom,
  dateRangeAtom,
  selectedChipAtom
} from '../atoms';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Typography
} from '@mui/material';

import { FontAwesomeSvgIcon, Tile } from 'components/shared';

import {
  useDownloadRealizationReport,
  useRealizeDutyLinesMutation
} from '../queries';
import { ReportDownloadingWSData } from '../../Reports/type';

type ReportBody = ReportDownloadingWSData['body'];

const eventBusUrl = '/maintenance/realize';

const RealizationProcedure = () => {
  const { t } = useTranslation();
  const { data: lastLockedMonth } = useQuery(['lastLockedMonth'], async () => {
    const { data } = await getLastLockedMonth();
    return data;
  });

  const [closeAllPrevious, setCloseAllPrevious] = useState<boolean>(false);
  const [dateRange] = useRecoilState(dateRangeAtom);

  const canDownloadReport = useRecoilValue(canDownloadReportAtom);

  const [progress, setProgress] = useState<DutyLinesProgress | null>(null);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const selectedChip = useRecoilValue(selectedChipAtom);

  const { mutate: realizeDutyLines, isError } = useRealizeDutyLinesMutation();
  const { data, refetch, isLoading } = useDownloadRealizationReport({
    validFrom: dateRange[0],
    validTo: startOfDay(addDays(dateRange[1] as Date, 1))
  });

  const downloadReport = (data: ReportBody) => {
    if (!data || !data.downloadUrl) return;
    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.setAttribute(
      'download',
      `${t('duties')}-${formatByLocale(new Date())}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadClick = () => {
    refetch();
  };

  const onRealizationProgress = (newProgress: DutyLinesProgress) => {
    if (newProgress?.hasFinished) {
      setIsInProgress(false);
      setProgress(newProgress);
      EventBus.unregister(eventBusUrl, onRealizationProgress);
    } else {
      setProgress(newProgress);
    }
  };

  const onChangeCheckbox = (evt, checked) => {
    setCloseAllPrevious(checked);
    setProgress(null);
  };

  const isCheckboxDisabled = () => {
    if (!dateRange[1] || !lastLockedMonth?.monthEnd) {
      return false;
    }

    const lastLockedDate = parse(
      lastLockedMonth.monthEnd,
      'yyyy-MM-dd',
      new Date()
    );

    return isBefore(dateRange[1], lastLockedDate);
  };

  const onClickRealize = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }

    setIsInProgress(true);
    EventBus.register(eventBusUrl, onRealizationProgress);

    await realizeDutyLines({
      startDate: dateRange[0],
      endDate: startOfDay(addDays(dateRange[1], 1)),
      lockMonth: closeAllPrevious
    });
  };

  const isValidDateRange =
    dateRange[0] &&
    dateRange[1] &&
    isValid(dateRange[0]) &&
    isValid(dateRange[1]);

  const dateRangeMisMatchError = Boolean(
    dateRange[0] && dateRange[1] && isAfter(dateRange[0], dateRange[1])
  );

  const futureWarning = dateRange[1] && isAfter(dateRange[1], new Date());

  useEffect(() => {
    if (data?.status === 'finished') {
      downloadReport(data);
    }
  }, [data]);

  useEffect(() => {
    if (selectedChip || isError) {
      setProgress(null);
      setIsInProgress(false);
      EventBus.unregister(eventBusUrl, onRealizationProgress);
    }
  }, [selectedChip, isError]);

  return (
    <Tile
      title={t('adminPortal.realization.realizationProcedure.name')}
      sx={{
        height: '100%',
        opacity: isValidDateRange && !dateRangeMisMatchError ? 1 : 0.5
      }}
      data-testid="realization"
    >
      <Typography variant="body2" sx={{ fontFamily: 'signikalight!important' }}>
        {t('adminPortal.realization.realizationProcedure.description')}
      </Typography>
      <Box pt={3}>
        <FormControlLabel
          disableTypography
          sx={{ fontSize: '13px', fontFamily: 'signikaregular' }}
          control={
            <Checkbox
              checked={closeAllPrevious}
              onChange={onChangeCheckbox}
              disabled={isCheckboxDisabled()}
            />
          }
          label={
            t('adminPortal.realization.realizationProcedure.lockDescription', {
              month: lastLockedMonth?.monthEnd
                ? format(
                    parse(lastLockedMonth.monthEnd, 'yyyy-MM-dd', new Date()),
                    'MM-yyyy'
                  )
                : '--'
            }) as string
          }
        />
      </Box>
      <Box pt={3}>
        {(!progress || progress?.hasFinished) && (
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              onClick={onClickRealize}
              disabled={
                isInProgress || !isValidDateRange || dateRangeMisMatchError
              }
            >
              {t('adminPortal.realization.realize')}
            </Button>
            {isValidDateRange && !dateRangeMisMatchError && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>
                  {`${formatByLocale(dateRange[0]!)} ${t(
                    'until'
                  )} ${formatByLocale(dateRange[1]!)}`}
                </Typography>
                {futureWarning && (
                  <Tooltip
                    title={
                      t(
                        'adminPortal.realization.realizationProcedure.warning'
                      ) || ''
                    }
                  >
                    <FontAwesomeSvgIcon
                      color="warning"
                      icon={faExclamationCircle}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        )}
        {progress && !progress.hasFinished && (
          <LinearProgress
            variant="determinate"
            value={(progress.dutyLinesProgress / progress.dutyLinesTotal) * 100}
          />
        )}
        {progress && (
          <Typography sx={{ mt: '16px!important' }}>
            {progress.dutyLinesProgress}/{progress.dutyLinesTotal},{' '}
            {progress.dutyLinesErrors} {t('errors')}
          </Typography>
        )}
        {progress?.hasFinished &&
          progress?.dutyLinesErrors > 0 &&
          canDownloadReport && (
            <Box mt={2}>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={handleDownloadClick}
              >
                {t('downloadExcelReport')}
              </Button>
            </Box>
          )}
      </Box>
    </Tile>
  );
};

export default RealizationProcedure;
